import React, { Fragment, useState, useEffect, useCallback } from "react";
import Breadcrumb from "../../../../../layout/breadcrumb";
import { getMisListByRole, middlewareEncryption } from "../../../../../api";
import { toast } from "react-toastify";
import SweetAlert from "sweetalert2";
import Select from "react-select";
import { Nav, NavItem, NavLink, TabContent, TabPane, Badge, Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import classnames from "classnames";
import { MaterialReactTable } from "material-react-table";
import "./role-reassignment.scss";

// Role config: label, key used in partnership schema, role_id in DB
// tab1Label/tab2Label = what the two list tabs are called for each role
// tab1ColHeader = column header in the first table
// tab1TypeLabel = type label used in CSV export for first-tab rows
// parentRoleId: role_id of the parent role; parentUserKey: field in user object holding parent's user_id
// grandParentRoleId / grandParentUserKey: one more level up (RM for SM)
const ROLE_CONFIG = [
  { label: "Sales Manager (SM)", key: "sm_id", role_id: 39, badge: "SM", color: "#6366f1", tab1Label: "Business Partnerships (BP)", tab2Label: "Business Aggregator (BA)", tab1ColHeader: "Partnership Name", tab1TypeLabel: "Partnership",
    parentRoleId: 40, parentRoleLabel: "City Manager", parentRoleBadge: "CM", parentUserKey: "reporting_to",
    grandParentRoleId: 34, grandParentRoleLabel: "Regional Manager", grandParentRoleBadge: "RM", grandParentUserKey: "reporting_to" },
  { label: "City Manager (CM)",  key: "cm_id", role_id: 40, badge: "CM", color: "#0ea5e9", tab1Label: "Sales Managers (SM)",          tab2Label: null,                           tab1ColHeader: "Sales Manager Name",   tab1TypeLabel: "Sales Manager",
    parentRoleId: 34, parentRoleLabel: "Regional Manager", parentRoleBadge: "RM", parentUserKey: "reporting_to",
    grandParentRoleId: null, grandParentRoleLabel: null, grandParentRoleBadge: null, grandParentUserKey: null },
  { label: "Regional Manager (RM)", key: "rm_id", role_id: 34, badge: "RM", color: "#f59e0b", tab1Label: "City Managers (CM)",         tab2Label: null,                           tab1ColHeader: "City Manager Name",   tab1TypeLabel: "City Manager",
    parentRoleId: null, parentRoleLabel: null, parentRoleBadge: null, parentUserKey: null,
    grandParentRoleId: null, grandParentRoleLabel: null, grandParentRoleBadge: null, grandParentUserKey: null },
];

const STEP_SELECT_ROLE = 1;
const STEP_SELECT_USERS = 2;
const STEP_PREVIEW = 3;
const STEP_SUCCESS = 4;

const RoleReassignment = () => {
  const [step, setStep] = useState(STEP_SELECT_ROLE);
  const [selectedRole, setSelectedRole] = useState(null);
  const [userList, setUserList] = useState([]);
  const [parentUserList, setParentUserList] = useState([]);      // CM list for SM; RM list for CM
  const [grandParentUserList, setGrandParentUserList] = useState([]); // RM list for SM (two levels up)
  const [oldUser, setOldUser] = useState(null);
  const [newUser, setNewUser] = useState(null);
  const [previewPartners, setPreviewPartners] = useState([]);
  const [previewConsolidators, setPreviewConsolidators] = useState([]);
  const [bpTotal, setBpTotal] = useState(0);
  const [baTotal, setBaTotal] = useState(0);

  const [rowSelectionBp, setRowSelectionBp] = useState({});
  const [rowSelectionBa, setRowSelectionBa] = useState({});
  const [isSelectingAll, setIsSelectingAll] = useState(false);
  const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
  const [isAllSelected, setIsAllSelected] = useState(false);
  const [allFetchedPartners, setAllFetchedPartners] = useState([]);
  const [allFetchedConsolidators, setAllFetchedConsolidators] = useState([]);

  const [activeTab, setActiveTab] = useState("1");
  const [bpPagination, setBpPagination] = useState({ pageIndex: 0, pageSize: 10 });
  const [baPagination, setBaPagination] = useState({ pageIndex: 0, pageSize: 10 });

  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resultSummary, setResultSummary] = useState(null);

  // Fetch current role users + parent + grandparent user lists
  const fetchUsersByRole = useCallback(async (role_id, parentRoleId, grandParentRoleId) => {
    setIsLoadingUsers(true);
    try {
      const res = await getMisListByRole({ role_id, all_data_flag: true });
      const users = res?.data?.result?.result || [];
      setUserList(users);

      // Fetch parent role users (CM for SM, RM for CM)
      if (parentRoleId) {
        try {
          const parentRes = await getMisListByRole({ role_id: parentRoleId, all_data_flag: true });
          setParentUserList(parentRes?.data?.result?.result || []);
        } catch {
          setParentUserList([]);
        }
      } else {
        setParentUserList([]);
      }

      // Fetch grandparent role users (RM for SM)
      if (grandParentRoleId) {
        try {
          const gpRes = await getMisListByRole({ role_id: grandParentRoleId, all_data_flag: true });
          setGrandParentUserList(gpRes?.data?.result?.result || []);
        } catch {
          setGrandParentUserList([]);
        }
      } else {
        setGrandParentUserList([]);
      }
    } catch (err) {
      toast.error("Failed to load users for selected role.");
      setUserList([]);
      setParentUserList([]);
      setGrandParentUserList([]);
    } finally {
      setIsLoadingUsers(false);
    }
  }, []);

  useEffect(() => {
    if (selectedRole) {
      setOldUser(null);
      setNewUser(null);
      setPreviewPartners([]);
      setPreviewConsolidators([]);
      setAllFetchedPartners([]);
      setAllFetchedConsolidators([]);
      setIsAllSelected(false);
      setBpTotal(0);
      setBaTotal(0);
      setParentUserList([]);
      setGrandParentUserList([]);
      fetchUsersByRole(selectedRole.role_id, selectedRole.parentRoleId, selectedRole.grandParentRoleId);
    }
  }, [selectedRole, fetchUsersByRole]);

  const getItemId = useCallback((item) => {
    if (!item) return "";
    return String(selectedRole?.key === "sm_id" ? item.partnership_id : item.user_id);
  }, [selectedRole]);

  // Fetch all partnerships and consolidators assigned to the old user
  const fetchPreview = useCallback(async () => {
    if (!oldUser || !selectedRole) return;
    setIsLoadingPreview(true);
    try {
      const payload = {
        role_key: selectedRole.key,
        [selectedRole.key]: oldUser.value,
        bpPageNo: bpPagination.pageIndex + 1,
        bpPageSize: bpPagination.pageSize,
        baPageNo: baPagination.pageIndex + 1,
        baPageSize: baPagination.pageSize,
      };
      const res = await middlewareEncryption(
        "/api/partnership/getEntitiesForRoleReassignment",
        payload
      );

      const result = res?.data?.result || {};

      const partnersList = Array.isArray(result.partners) ? result.partners : [];
      setPreviewPartners(partnersList);
      setAllFetchedPartners(prev => {
        const map = new Map(prev.map(p => [getItemId(p), p]));
        partnersList.forEach(p => map.set(getItemId(p), p));
        return Array.from(map.values());
      });
      setBpTotal(result.partnersTotal || partnersList.length);

      const consolidatorsList = Array.isArray(result.consolidators) ? result.consolidators : [];
      setPreviewConsolidators(consolidatorsList);
      setAllFetchedConsolidators(prev => {
        const map = new Map(prev.map(p => [p.consolidator_id, p]));
        consolidatorsList.forEach(p => map.set(p.consolidator_id, p));
        return Array.from(map.values());
      });
      setBaTotal(result.consolidatorsTotal || consolidatorsList.length);

    } catch (err) {
      // Graceful fallback — show empty but still let user proceed
      setPreviewPartners([]);
      setPreviewConsolidators([]);
      setBpTotal(0);
      setBaTotal(0);
    } finally {
      setIsLoadingPreview(false);
    }
  }, [oldUser, selectedRole, bpPagination.pageIndex, bpPagination.pageSize, baPagination.pageIndex, baPagination.pageSize, getItemId]);

  // Re-fetch preview when pagination changes
  useEffect(() => {
    if (step === STEP_PREVIEW) {
      fetchPreview();
    }
  }, [step, fetchPreview]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) setActiveTab(tab);
  };

  const bpColumns = React.useMemo(() => {
    if (selectedRole?.key === "cm_id") {
      return [
        {
          accessorKey: "name",
          header: "Sales Manager Name",
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {row.original.assignment_sync && <span title="Pending Reassignment Request" style={{ cursor: 'help' }}>🔒</span>}
              <span style={{ color: row.original.assignment_sync ? '#94a3b8' : 'inherit', fontWeight: 500 }}>{row.original.name}</span>
            </div>
          )
        },
        { accessorKey: "email", header: "Email" },
        { accessorKey: "phone_no", header: "Phone" },
        {
          accessorKey: "bp_count",
          header: "Managed BPs",
          Cell: ({ row }) => (
            <Badge color="info" pill style={{ fontSize: '12px', padding: '4px 10px' }}>
              {row.original.bp_count ?? 0}
            </Badge>
          )
        },
        {
          accessorKey: "is_active",
          header: "Status",
          Cell: ({ row }) => (
            row.original.is_active ?
              <Badge color="success">Active</Badge> :
              <Badge color="danger">Inactive</Badge>
          )
        }
      ];
    }

    if (selectedRole?.key === "rm_id") {
      return [
        {
          accessorKey: "name",
          header: "City Manager Name",
          Cell: ({ row }) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {row.original.assignment_sync && <span title="Pending Reassignment Request" style={{ cursor: 'help' }}>🔒</span>}
              <span style={{ color: row.original.assignment_sync ? '#94a3b8' : 'inherit', fontWeight: 500 }}>{row.original.name}</span>
            </div>
          )
        },
        { accessorKey: "email", header: "Email" },
        { accessorKey: "phone_no", header: "Phone" },
        {
          accessorKey: "sm_count",
          header: "Managed SMs",
          Cell: ({ row }) => (
            <Badge color="primary" pill style={{ fontSize: '12px', padding: '4px 10px' }}>
              {row.original.sm_count ?? 0}
            </Badge>
          )
        },
        {
          accessorKey: "bp_count",
          header: "Total BPs",
          Cell: ({ row }) => (
            <Badge color="info" pill style={{ fontSize: '12px', padding: '4px 10px' }}>
              {row.original.bp_count ?? 0}
            </Badge>
          )
        },
        {
          accessorKey: "is_active",
          header: "Status",
          Cell: ({ row }) => (
            row.original.is_active ?
              <Badge color="success">Active</Badge> :
              <Badge color="danger">Inactive</Badge>
          )
        }
      ];
    }

    return [
      { 
        accessorKey: "name", 
        header: selectedRole?.tab1ColHeader || "Partnership Name",
        Cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {row.original.assignment_sync && <span title="Pending Reassignment Request" style={{ cursor: 'help' }}>🔒</span>}
            <span style={{ color: row.original.assignment_sync ? '#94a3b8' : 'inherit' }}>{row.original.name}</span>
          </div>
        )
      },
      { accessorKey: "partnership_code", header: "Code" },
      { accessorKey: "city", header: "City" },
      { accessorKey: "state", header: "State" },
      {
        accessorKey: "is_active",
        header: "Status",
        Cell: ({ row }) => (
          row.original.is_active ?
            <Badge color="success">Active</Badge> :
            <Badge color="danger">Inactive</Badge>
        )
      }
    ];
  }, [selectedRole]);

  const baColumns = React.useMemo(
    () => [
      { 
        accessorKey: "name", 
        header: "Associate Name",
        Cell: ({ row }) => (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {row.original.assignment_sync && <span title="Pending Reassignment Request" style={{ cursor: 'help' }}>🔒</span>}
            <span style={{ color: row.original.assignment_sync ? '#94a3b8' : 'inherit' }}>{row.original.name}</span>
          </div>
        )
      },
      { accessorKey: "code", header: "Code" },
      {
        accessorKey: "is_active",
        header: "Status",
        Cell: ({ row }) => (
          row.original.is_active ?
            <Badge color="success">Active</Badge> :
            <Badge color="danger">Inactive</Badge>
        )
      }
    ],
    []
  );

  const handleRoleSelect = (role) => {
    setSelectedRole(role);
    setActiveTab("1");
    setStep(STEP_SELECT_USERS);
  };


  const handleSelectAllData = async () => {
    if (isAllSelected) {
      setRowSelectionBp({});
      setRowSelectionBa({});
      setIsAllSelected(false);
      return;
    }

    setIsSelectingAll(true);
    try {
      const payload = {
        role_key: selectedRole.key,
        [selectedRole.key]: oldUser.value,
        all_data_flag: true
      };
      const res = await middlewareEncryption("/api/partnership/getEntitiesForRoleReassignment", payload);
      const result = res?.data?.result || {};
      
      const partnersList = Array.isArray(result.partners) ? result.partners : [];
      setAllFetchedPartners(prev => {
        const map = new Map(prev.map(p => [getItemId(p), p]));
        partnersList.forEach(p => map.set(getItemId(p), p));
        return Array.from(map.values());
      });

      const consolidatorsList = Array.isArray(result.consolidators) ? result.consolidators : [];
      setAllFetchedConsolidators(prev => {
        const map = new Map(prev.map(p => [p.consolidator_id, p]));
        consolidatorsList.forEach(p => map.set(p.consolidator_id, p));
        return Array.from(map.values());
      });

      const newRowSelectionBp = {};
      partnersList.forEach(p => {
        if (!p.assignment_sync) {
          newRowSelectionBp[getItemId(p)] = true;
        }
      });
      setRowSelectionBp(newRowSelectionBp);

      const newRowSelectionBa = {};
      consolidatorsList.forEach(p => {
        if (!p.assignment_sync) {
          newRowSelectionBa[p.consolidator_id] = true;
        }
      });
      setRowSelectionBa(newRowSelectionBa);
      
      setIsAllSelected(true);
      toast.success("Selected all records across all pages");
    } catch (err) {
      toast.error("Failed to select all records");
    } finally {
      setIsSelectingAll(false);
    }
  };

  const handleOpenReviewModal = () => {
    if (!oldUser || !newUser || !selectedRole) return;

    let bp_ids = Object.keys(rowSelectionBp);
    let ba_ids = Object.keys(rowSelectionBa);
    let affectedBpCount = bp_ids.length;
    let affectedBaCount = ba_ids.length;

    if (affectedBpCount === 0 && affectedBaCount === 0) {
      toast.warning("Please select at least one record to re-assign, or click 'Transfer All Records'.");
      return;
    }

    setIsReviewModalOpen(true);
  };

  const handleDownloadSelectedData = () => {
    let bp_ids = Object.keys(rowSelectionBp);
    let ba_ids = Object.keys(rowSelectionBa);

    let csvContent = "data:text/csv;charset=utf-8,";

    if (selectedRole?.key === 'cm_id') {
      csvContent += "Type,Name,Email,Phone,Managed BPs,Status\n";
      bp_ids.forEach(id => {
        const p = allFetchedPartners.find(x => String(x.user_id) === String(id));
        if (p) {
          csvContent += `Sales Manager,"${p.name || ''}","${p.email || ''}","${p.phone_no || ''}",${p.bp_count || 0},"${p.is_active ? 'Active' : 'Inactive'}"\n`;
        } else {
          csvContent += `Sales Manager,ID ${id},,,,\n`;
        }
      });
    } else if (selectedRole?.key === 'rm_id') {
      csvContent += "Type,Name,Email,Phone,Managed SMs,Total BPs,Status\n";
      bp_ids.forEach(id => {
        const p = allFetchedPartners.find(x => String(x.user_id) === String(id));
        if (p) {
          csvContent += `City Manager,"${p.name || ''}","${p.email || ''}","${p.phone_no || ''}",${p.sm_count || 0},${p.bp_count || 0},"${p.is_active ? 'Active' : 'Inactive'}"\n`;
        } else {
          csvContent += `City Manager,ID ${id},,,,,\n`;
        }
      });
    } else {
      const tab1TypeLabel = selectedRole?.tab1TypeLabel || "Partnership";
      csvContent += "Type,Name,Code,City\n";

      bp_ids.forEach(id => {
        const p = allFetchedPartners.find(x => String(x.partnership_id) === String(id));
        if (p) {
          csvContent += `${tab1TypeLabel},"${p.name}","${p.partnership_code || ''}","${p.city || ''}"\n`;
        } else {
          csvContent += `${tab1TypeLabel},ID ${id},,\n`;
        }
      });

      ba_ids.forEach(id => {
        const p = allFetchedConsolidators.find(x => String(x.consolidator_id) === String(id));
        if (p) {
          csvContent += `Aggregator,"${p.name}","${p.code || ''}","${p.city || ''}"\n`;
        } else {
          csvContent += `Aggregator,ID ${id},,\n`;
        }
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `selected_${selectedRole?.badge || 'records'}_for_transfer.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleGoToPreview = async () => {
    if (!oldUser || !newUser) {
      toast.warning("Please select both the outgoing and incoming user.");
      return;
    }
    if (oldUser.value === newUser.value) {
      toast.error("Old and new user cannot be the same person.");
      return;
    }
    await fetchPreview();
    setStep(STEP_PREVIEW);
  };

  const handleConfirmReassign = async () => {
    if (!oldUser || !newUser || !selectedRole) return;

    let bp_ids = Object.keys(rowSelectionBp);
    let ba_ids = Object.keys(rowSelectionBa);

    setIsSubmitting(true);
    try {
      const payload = {
        old_user_id: oldUser.value,
        new_user_id: newUser.value,
        role_key: selectedRole.key,
        bp_ids: bp_ids,
        ba_ids: ba_ids
      };
      const res = await middlewareEncryption(
        "/api/partnership/requestRoleReassignmentTicket",
        payload
      );
      
      setIsReviewModalOpen(false);
      
      const ticketNumber = res?.data?.result?.ticketNumber;
      
      await SweetAlert.fire({
        title: "Ticket Generated Successfully",
        html: `<p>A ticket has been generated and sent to HO for approval.</p>
               <h3 style="color: #6366f1; margin: 15px 0;">${ticketNumber}</h3>
               <p>Status: <Badge color="warning">Pending Approval</Badge></p>`,
        icon: "success",
        confirmButtonText: "OK",
        confirmButtonColor: "#6366f1",
      });
      
      handleReset();
    } catch (err) {
      const errMsg = err?.response?.data?.error || "Failed to generate ticket. Please try again.";
      toast.error(errMsg);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleReset = () => {
    setStep(STEP_SELECT_ROLE);
    setSelectedRole(null);
    setOldUser(null);
    setNewUser(null);
    setPreviewPartners([]);
    setPreviewConsolidators([]);
    setAllFetchedPartners([]);
    setAllFetchedConsolidators([]);
    setIsAllSelected(false);
    setBpTotal(0);
    setBaTotal(0);
    setBpPagination({ pageIndex: 0, pageSize: 10 });
    setBaPagination({ pageIndex: 0, pageSize: 10 });
    setRowSelectionBp({});
    setRowSelectionBa({});
    setActiveTab("1");
    setResultSummary(null);
    setParentUserList([]);
    setGrandParentUserList([]);
  };

  /**
   * Build the full hierarchy chain for a selected user.
   * Returns an array of { badge, label, name } objects representing
   * every role ABOVE the currently selected user, top-down.
   * e.g. for SM: [ { badge:'CM', label:'City Manager', name:'Ravi Kumar' }, { badge:'RM', label:'Regional Manager', name:'Amit Sharma' }, { badge:'HO', label:'Head Office', name:'Head Office' } ]
   */
  const getHierarchy = (userOption) => {
    if (!userOption || !selectedRole) return [];
    const chain = [];
    const rawUser = userList.find((u) => u.user_id === userOption.value);

    // Level 1: parent (CM for SM, RM for CM)
    if (selectedRole.parentUserKey && rawUser) {
      const parentId = rawUser.reporting_to || rawUser.created_by_user_id || rawUser.created_by;
      const parentUser = parentId ? parentUserList.find((u) => u.user_id === Number(parentId)) : null;
      chain.push({
        badge: selectedRole.parentRoleBadge,
        label: selectedRole.parentRoleLabel,
        name: parentUser ? parentUser.name : (parentId ? `ID ${parentId}` : null),
        color: selectedRole.badge === 'SM' ? '#0ea5e9' : '#f59e0b',
        rawUser: parentUser,
        rawId: parentId,
      });

      // Level 2: grandparent (RM for SM)
      if (selectedRole.grandParentUserKey && parentUser) {
        const grandParentId = parentUser.reporting_to || parentUser.created_by_user_id || parentUser.created_by;
        const grandParentUser = grandParentId ? grandParentUserList.find((u) => u.user_id === Number(grandParentId)) : null;
        chain.push({
          badge: selectedRole.grandParentRoleBadge,
          label: selectedRole.grandParentRoleLabel,
          name: grandParentUser ? grandParentUser.name : (grandParentId ? `ID ${grandParentId}` : null),
          color: '#f59e0b',
          rawId: grandParentId,
        });
      }
    }

    // Always add HO at the top
    chain.push({ badge: 'HO', label: 'Head Office', name: 'Head Office', color: '#8b5cf6', isStatic: true });
    return chain.filter(c => c.name); // hide levels where name couldn't be resolved
  };

  const userOptions = userList.map((u) => ({
    value: u.user_id,
    label: u.name,
    email: u.email,
  }));

  const newUserOptions = userOptions.filter(
    (u) => !oldUser || u.value !== oldUser.value
  );
  const oldUserOptions = userOptions.filter(
    (u) => !newUser || u.value !== newUser.value
  );

  const formatOptionLabel = ({ label, email }) => (
    <div className="rr-option">
      <span className="rr-option__name">{label}</span>
      {email && <span className="rr-option__email">{email}</span>}
    </div>
  );

  // Build hierarchy chains for the preview step
  const oldUserHierarchy = getHierarchy(oldUser);
  const newUserHierarchy = getHierarchy(newUser);

  return (
    <Fragment>
      <Breadcrumb
        parent="Affiliations"
        title="Role Re-assignment"
        child="Transfer Role"
      />

      <div className="rr-container">
        {/* ── Stepper ─────────────────────────────────── */}
        <div className="rr-stepper">
          {[
            { num: 1, label: "Select Role" },
            { num: 2, label: "Choose Users" },
            { num: 3, label: "Preview & Confirm" },
            { num: 4, label: "Done" },
          ].map((s, idx) => (
            <React.Fragment key={s.num}>
              <div
                className={`rr-step ${step === s.num ? "active" : ""} ${step > s.num ? "done" : ""
                  }`}
              >
                <div className="rr-step__circle">
                  {step > s.num ? (
                    <svg viewBox="0 0 20 20" fill="currentColor">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  ) : (
                    s.num
                  )}
                </div>
                <span className="rr-step__label">{s.label}</span>
              </div>
              {idx < 3 && <div className={`rr-step__line ${step > s.num ? "done" : ""}`} />}
            </React.Fragment>
          ))}
        </div>

        {/* ── STEP 1: Select Role ──────────────────────── */}
        {step === STEP_SELECT_ROLE && (
          <div className="rr-card rr-fade-in">
            <div className="rr-card__header">
              <div className="rr-card__icon rr-icon-role">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
              </div>
              <div>
                <h2 className="rr-card__title">Select the Role to Re-assign</h2>
                <p className="rr-card__subtitle">
                  Choose which role's assignments you want to transfer across all Business Partnerships
                </p>
              </div>
            </div>

            <div className="rr-role-grid">
              {ROLE_CONFIG.map((role) => (
                <button
                  key={role.key}
                  className="rr-role-card"
                  onClick={() => handleRoleSelect(role)}
                  style={{ "--role-color": role.color }}
                >
                  <div className="rr-role-card__badge">{role.badge}</div>
                  <div className="rr-role-card__label">{role.label}</div>
                  <div className="rr-role-card__desc">
                    {role.badge === "SM" && "Handles direct BP relationships & sales targets"}
                    {role.badge === "CM" && "Manages multiple SMs and city-level partnerships"}
                    {role.badge === "RM" && "Oversees regional CM and BP hierarchy"}
                  </div>
                  <div className="rr-role-card__arrow">→</div>
                </button>
              ))}
            </div>

            <div className="rr-info-box">
              <svg viewBox="0 0 20 20" fill="currentColor" className="rr-info-box__icon">
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
              <p>
                This tool allows you to <strong>bulk-re-assign</strong> all Business Partnerships from a resigned or transferred
                user to their replacement — without manually editing each partnership.
              </p>
            </div>
          </div>
        )}

        {/* ── STEP 2: Select Users ─────────────────────── */}
        {step === STEP_SELECT_USERS && selectedRole && (
          <div className="rr-card rr-fade-in">
            <div className="rr-card__header">
              <button className="rr-btn-back" onClick={() => setStep(STEP_SELECT_ROLE)}>
                ← Back
              </button>
              <div
                className="rr-badge-pill"
                style={{ background: selectedRole.color }}
              >
                {selectedRole.badge}
              </div>
              <div>
                <h2 className="rr-card__title">Choose: {selectedRole.label}</h2>
                <p className="rr-card__subtitle">
                  Map the <strong>outgoing</strong> user (resigned/transferred) to the <strong>replacement</strong> user
                </p>
              </div>
            </div>

            {isLoadingUsers ? (
              <div className="rr-loading">
                <div className="rr-spinner" />
                <span>Loading {selectedRole.label} users…</span>
              </div>
            ) : (
              <div className="rr-user-grid">
                {/* Outgoing User */}
                <div className="rr-user-card rr-user-card--out">
                  <div className="rr-user-card__header">
                    <div className="rr-user-tag rr-user-tag--out">Outgoing (Resigned)</div>
                    <div className="rr-user-card__hint">Select the user who has left or is being replaced</div>
                  </div>
                  <Select
                    options={oldUserOptions}
                    value={oldUser}
                    onChange={setOldUser}
                    placeholder={`Search ${selectedRole.label}…`}
                    isSearchable
                    isClearable
                    formatOptionLabel={formatOptionLabel}
                    classNamePrefix="rr-select"
                    noOptionsMessage={() => "No users found"}
                  />
                  {oldUser && (
                    <div className="rr-user-preview rr-user-preview--out">
                      <div className="rr-user-avatar rr-user-avatar--out">
                        {oldUser.label.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="rr-user-preview__name">{oldUser.label}</div>
                        <div className="rr-user-preview__email">{oldUser.email || "No email"}</div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Arrow */}
                <div className="rr-transfer-arrow">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                    <line x1="5" y1="12" x2="19" y2="12" />
                    <polyline points="12 5 19 12 12 19" />
                  </svg>
                  <span>TRANSFER</span>
                </div>

                {/* Incoming User */}
                <div className="rr-user-card rr-user-card--in">
                  <div className="rr-user-card__header">
                    <div className="rr-user-tag rr-user-tag--in">Incoming (Replacement)</div>
                    <div className="rr-user-card__hint">Select the new user taking over the role</div>
                  </div>
                  <Select
                    options={newUserOptions}
                    value={newUser}
                    onChange={setNewUser}
                    placeholder={`Search ${selectedRole.label}…`}
                    isSearchable
                    isClearable
                    formatOptionLabel={formatOptionLabel}
                    classNamePrefix="rr-select"
                    noOptionsMessage={() => "No users found"}
                  />
                  {newUser && (
                    <div className="rr-user-preview rr-user-preview--in">
                      <div className="rr-user-avatar rr-user-avatar--in">
                        {newUser.label.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div className="rr-user-preview__name">{newUser.label}</div>
                        <div className="rr-user-preview__email">{newUser.email || "No email"}</div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="rr-actions">
              <button className="rr-btn rr-btn--secondary" onClick={() => setStep(STEP_SELECT_ROLE)}>
                ← Back
              </button>
              <button
                className="rr-btn rr-btn--primary"
                onClick={handleGoToPreview}
                disabled={!oldUser || !newUser || isLoadingUsers}
              >
                {isLoadingPreview ? (
                  <>
                    <div className="rr-spinner rr-spinner--sm" /> Loading Preview…
                  </>
                ) : (
                  "Preview Partnerships →"
                )}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 3: Preview ──────────────────────────── */}
        {step === STEP_PREVIEW && (
          <div className="rr-card rr-fade-in">
            <div className="rr-card__header">
              <button className="rr-btn-back" onClick={() => setStep(STEP_SELECT_USERS)}>
                ← Back
              </button>
              <div>
                <h2 className="rr-card__title">Preview & Confirm Re-assignment</h2>
                <p className="rr-card__subtitle">
                  Review all Business Partnerships that will be updated before confirming
                </p>
              </div>
            </div>

            {/* Summary strip */}
            <div className="rr-summary-strip">
              <div className="rr-summary-item">
                <span className="rr-summary-item__label">Role</span>
                <span
                  className="rr-summary-item__val rr-badge-pill"
                  style={{ background: selectedRole?.color }}
                >
                  {selectedRole?.badge} — {selectedRole?.label}
                </span>
              </div>
              <div className="rr-summary-item">
                <span className="rr-summary-item__label">{selectedRole?.tab1Label?.split(' (')[0] || 'Records'} Affected</span>
                <span className="rr-summary-item__val rr-summary-count">{bpTotal}</span>
              </div>
              {selectedRole?.tab2Label && (
                <div className="rr-summary-item">
                  <span className="rr-summary-item__label">{selectedRole?.tab2Label?.split(' (')[0] || 'BA'} Affected</span>
                  <span className="rr-summary-item__val rr-summary-count">{baTotal}</span>
                </div>
              )}
              <div className="rr-summary-item">
                <span className="rr-summary-item__label">Total Records Affected</span>
                <span className="rr-summary-item__val rr-summary-count">{bpTotal + (selectedRole?.tab2Label ? baTotal : 0)}</span>
              </div>
            </div>

            {/* ── Hierarchy Panel ── */}
            {(oldUserHierarchy.length > 0 || newUserHierarchy.length > 0) && (
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', margin: '20px 0' }}>
                {/* Outgoing hierarchy */}
                <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #ef4444, #f87171)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>{selectedRole?.badge}</span>
                    <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{oldUser?.label}</span>
                    <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>Outgoing</span>
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    {oldUserHierarchy.map((level, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: idx < oldUserHierarchy.length - 1 ? '1px dashed #fecaca' : 'none' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: '2px', height: idx === 0 ? '0' : '8px', background: '#fca5a5' }} />
                          <span style={{ background: level.color, color: '#fff', fontSize: '10px', fontWeight: '700', padding: '3px 7px', borderRadius: '20px', whiteSpace: 'nowrap' }}>{level.badge}</span>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{level.label}</div>
                          <div style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>{level.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Incoming hierarchy */}
                <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '12px', overflow: 'hidden' }}>
                  <div style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ background: 'rgba(255,255,255,0.25)', color: '#fff', fontSize: '11px', fontWeight: '700', padding: '2px 8px', borderRadius: '20px' }}>{selectedRole?.badge}</span>
                    <span style={{ color: '#fff', fontWeight: '600', fontSize: '14px' }}>{newUser?.label}</span>
                    <span style={{ marginLeft: 'auto', color: 'rgba(255,255,255,0.8)', fontSize: '12px' }}>Incoming</span>
                  </div>
                  <div style={{ padding: '12px 16px' }}>
                    {newUserHierarchy.map((level, idx) => (
                      <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: idx < newUserHierarchy.length - 1 ? '1px dashed #bbf7d0' : 'none' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <div style={{ width: '2px', height: idx === 0 ? '0' : '8px', background: '#86efac' }} />
                          <span style={{ background: level.color, color: '#fff', fontSize: '10px', fontWeight: '700', padding: '3px 7px', borderRadius: '20px', whiteSpace: 'nowrap' }}>{level.badge}</span>
                        </div>
                        <div>
                          <div style={{ fontSize: '11px', color: '#9ca3af', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{level.label}</div>
                          <div style={{ fontSize: '14px', color: '#374151', fontWeight: '600' }}>{level.name}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Tabs for BP and BA */}
                        {/* Transfer Mode Selection */}
            <div className="rr-transfer-mode-selector" style={{ margin: "20px 0", padding: "15px", backgroundColor: "#f8fafc", borderRadius: "8px", border: "1px solid #e2e8f0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <h5 style={{ margin: "0 0 5px 0", fontSize: "16px", fontWeight: "600" }}>Transfer Action</h5>
                <p style={{ margin: 0, fontSize: "14px", color: "#64748b" }}>Select specific checkboxes below, or automatically select all records across all pages.</p>
              </div>
              <button 
                className="rr-btn rr-btn--primary" 
                onClick={handleSelectAllData}
                disabled={isSelectingAll || (bpTotal === 0 && baTotal === 0)}
              >
                {isSelectingAll ? (
                  <><div className="rr-spinner rr-spinner--sm" /> Processing…</>
                ) : isAllSelected ? (
                  "Remove all records"
                ) : (
                  "Transfer All Records"
                )}
              </button>
            </div>

            <Nav tabs>
              <NavItem>
                <NavLink
                  className={classnames({ active: activeTab === '1' })}
                  onClick={() => { toggleTab('1'); }}
                  style={{ cursor: "pointer" }}
                >
                  {selectedRole?.tab1Label || 'Business Partnerships (BP)'}
                </NavLink>
              </NavItem>
              {selectedRole?.tab2Label && (
                <NavItem>
                  <NavLink
                    className={classnames({ active: activeTab === '2' })}
                    onClick={() => { toggleTab('2'); }}
                    style={{ cursor: "pointer" }}
                  >
                    {selectedRole?.tab2Label}
                  </NavLink>
                </NavItem>
              )}
            </Nav>

            <TabContent activeTab={activeTab} className="mt-4">
              {/* BP Tab Content */}
              <TabPane tabId="1">
                {isLoadingPreview && previewPartners.length === 0 ? (
                  <div className="rr-loading">
                    <div className="rr-spinner" />
                    <span>Fetching affected {selectedRole?.tab1Label || 'records'}…</span>
                  </div>
                ) : (
                  <MaterialReactTable
                    columns={bpColumns}
                    data={previewPartners}
                    initialState={{ showColumnFilters: false, density: 'compact' }}
                    enableGlobalFilter={false}
                    enableColumnActions={false}
                    enableColumnFilters={false}
                    enableSorting={false}
                    enableTopToolbar={false}
                    enableBottomToolbar={true}
                    enableSelectAll={false}
                    manualPagination
                    rowCount={bpTotal}
                    enableRowSelection={(row) => !row.original.assignment_sync}
                    getRowId={(row) => getItemId(row)}
                    onRowSelectionChange={(updater) => {
                      setRowSelectionBp(prev => typeof updater === 'function' ? updater(prev) : updater);
                      setIsAllSelected(false);
                    }}
                    state={{
                      pagination: bpPagination,
                      isLoading: isLoadingPreview,
                      rowSelection: rowSelectionBp
                    }}
                    onPaginationChange={setBpPagination}
                  />
                )}
              </TabPane>

              {/* BA Tab Content */}
              {selectedRole?.tab2Label && (
                <TabPane tabId="2">
                  {isLoadingPreview && previewConsolidators.length === 0 ? (
                    <div className="rr-loading">
                      <div className="rr-spinner" />
                      <span>Fetching affected associates…</span>
                    </div>
                  ) : (
                    <MaterialReactTable
                      columns={baColumns}
                      data={previewConsolidators}
                      initialState={{ showColumnFilters: false, density: 'compact' }}
                      enableGlobalFilter={false}
                      enableColumnActions={false}
                      enableColumnFilters={false}
                      enableSorting={false}
                      enableTopToolbar={false}
                      enableBottomToolbar={true}
                      enableSelectAll={false}
                      manualPagination
                      rowCount={baTotal}
                      enableRowSelection={(row) => !row.original.assignment_sync}
                      getRowId={(row) => row.consolidator_id}
                      onRowSelectionChange={(updater) => {
                        setRowSelectionBa(prev => typeof updater === 'function' ? updater(prev) : updater);
                        setIsAllSelected(false);
                      }}
                      state={{
                        pagination: baPagination,
                        isLoading: isLoadingPreview,
                        rowSelection: rowSelectionBa
                      }}
                      onPaginationChange={setBaPagination}
                    />
                  )}
                </TabPane>
              )}
            </TabContent>

            <div className="rr-confirm-note">
              <svg viewBox="0 0 20 20" fill="currentColor">
                <path
                  fillRule="evenodd"
                  d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                  clipRule="evenodd"
                />
              </svg>
              <span>
                Confirming will request to update the <strong>{selectedRole?.badge} mapping</strong> on{" "}
                <strong>{Object.keys(rowSelectionBp).length + Object.keys(rowSelectionBa).length}</strong> selected {selectedRole?.key === 'sm_id' ? 'record(s)' : selectedRole?.key === 'cm_id' ? 'Sales Manager(s)' : 'City Manager(s)'}. This will generate a ticket for Head Office approval.
              </span>
            </div>

            <div className="rr-actions">
              <button
                className="rr-btn rr-btn--secondary"
                onClick={() => setStep(STEP_SELECT_USERS)}
                disabled={isSubmitting}
              >
                ← Back
              </button>
              <button className="rr-btn rr-btn--primary" onClick={handleOpenReviewModal} disabled={isSubmitting}>
                {isSubmitting ? "Generating Ticket…" : "Review & Submit"}
              </button>
            </div>
          </div>
        )}

        {/* ── STEP 4: Success ──────────────────────────── */}
        {step === STEP_SUCCESS && resultSummary && (
          <div className="rr-card rr-fade-in rr-success-card">
            <div className="rr-success-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                <polyline points="22 4 12 14.01 9 11.01" />
              </svg>
            </div>
            <h2 className="rr-success-title">Re-assignment Successful!</h2>
            <p className="rr-success-subtitle">
              All partnerships have been updated successfully.
            </p>
            <div className="rr-success-details">
              <div className="rr-success-detail-row">
                <span>Role</span>
                <strong>{resultSummary.roleLabel}</strong>
              </div>
              <div className="rr-success-detail-row">
                <span>From</span>
                <strong className="rr-text-out">{resultSummary.oldUserName}</strong>
              </div>
              <div className="rr-success-detail-row">
                <span>To</span>
                <strong className="rr-text-in">{resultSummary.newUserName}</strong>
              </div>
              <div className="rr-success-detail-row">
                <span>Partnerships Updated</span>
                <strong className="rr-text-count">{resultSummary.updated}</strong>
              </div>
            </div>
            <button className="rr-btn rr-btn--primary rr-btn--lg" onClick={handleReset}>
              ← Perform Another Re-assignment
            </button>
          </div>
        )}
      </div>
      <Modal isOpen={isReviewModalOpen} toggle={() => setIsReviewModalOpen(false)} centered size="lg" backdrop="static" fade={true} contentClassName="border-0 shadow-lg" style={{ borderRadius: '16px', overflow: 'hidden' }}>
        <div style={{ padding: '0', background: '#ffffff', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)' }}>
          {/* Header */}
          <div style={{ background: 'linear-gradient(135deg, #4f46e5 0%, #6366f1 100%)', padding: '24px 32px', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '22px', fontWeight: '600', letterSpacing: '0.5px' }}>Review & Submit Ticket</h3>
              <p style={{ margin: '6px 0 0 0', fontSize: '14px', opacity: '0.9' }}>
                Transaction Type: {selectedRole?.key === 'sm_id' ? 'BP & BA Re-assignment' : selectedRole?.key === 'cm_id' ? 'Sales Manager (SM) Re-assignment' : 'City Manager (CM) Re-assignment'}
              </p>
            </div>
            <button onClick={() => setIsReviewModalOpen(false)} style={{ background: 'rgba(255,255,255,0.2)', border: 'none', color: 'white', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.2s' }}>✕</button>
          </div>
          
          {/* Body */}
          <div style={{ padding: '32px', background: '#f8fafc' }}>
            {/* User Transfer Path */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '24px', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>From User</div>
                <div style={{ fontWeight: '600', color: '#334155', fontSize: '18px' }}>{oldUser?.label}</div>
              </div>
              <div style={{ padding: '0 20px', color: '#cbd5e1' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg>
              </div>
              <div style={{ textAlign: 'center', flex: 1 }}>
                <div style={{ fontSize: '13px', color: '#64748b', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px', fontWeight: '600' }}>To User</div>
                <div style={{ fontWeight: '600', color: '#4f46e5', fontSize: '18px' }}>{newUser?.label}</div>
              </div>
            </div>

            {/* Counts */}
            <div style={{ display: 'flex', gap: '16px', marginBottom: '24px' }}>
              <div style={{ flex: 1, background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                <div>
                  <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>
                    {selectedRole?.key === 'sm_id' ? 'Selected BPs' : selectedRole?.key === 'cm_id' ? 'Selected Sales Managers' : 'Selected City Managers'}
                  </div>
                  <div style={{ fontSize: '28px', fontWeight: '700', color: '#334155' }}>{Object.keys(rowSelectionBp).length}</div>
                </div>
                <div style={{ background: '#e0e7ff', color: '#4f46e5', padding: '12px', borderRadius: '10px' }}>
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                </div>
              </div>
              {selectedRole?.key === 'sm_id' ? (
                <div style={{ flex: 1, background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Selected BAs</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#334155' }}>{Object.keys(rowSelectionBa).length}</div>
                  </div>
                  <div style={{ background: '#e0e7ff', color: '#4f46e5', padding: '12px', borderRadius: '10px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                  </div>
                </div>
              ) : (
                <div style={{ flex: 1, background: 'white', padding: '20px', borderRadius: '12px', border: '1px solid #e2e8f0', display: 'flex', alignItems: 'center', justifyContent: 'space-between', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <div>
                    <div style={{ fontSize: '14px', color: '#64748b', fontWeight: '600', marginBottom: '4px' }}>Associated BPs</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#0ea5e9' }}>
                      {allFetchedPartners.reduce((acc, p) => rowSelectionBp[String(p.user_id)] ? acc + (Number(p.bp_count) || 0) : acc, 0)}
                    </div>
                  </div>
                  <div style={{ background: '#e0f2fe', color: '#0ea5e9', padding: '12px', borderRadius: '10px' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
                  </div>
                </div>
              )}
            </div>

            {/* Preview List */}
            <div style={{ background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
              <div style={{ padding: '16px 20px', background: '#f1f5f9', borderBottom: '1px solid #e2e8f0', fontSize: '14px', fontWeight: '700', color: '#475569', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span>Selected Items Preview</span>
                <button onClick={handleDownloadSelectedData} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#4f46e5', fontWeight: '600', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', padding: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>
                  Download Full List
                </button>
              </div>
              <div style={{ padding: '0' }}>
                {selectedRole?.key !== 'sm_id' ? (
                  Object.keys(rowSelectionBp).slice(0, 5).map((id) => {
                    const p = allFetchedPartners.find(x => String(x.user_id) === String(id));
                    return (
                      <div key={`mgr-${id}`} style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <span style={{ background: selectedRole?.key === 'cm_id' ? '#e0f2fe' : '#fef3c7', color: selectedRole?.key === 'cm_id' ? '#0ea5e9' : '#d97706', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontWeight: '700' }}>
                          {selectedRole?.key === 'cm_id' ? 'SM' : 'CM'}
                        </span>
                        <div>
                          <span style={{ fontSize: '15px', color: '#334155', fontWeight: '600' }}>{p ? p.name : `ID ${id}`}</span>
                          {p?.email && <div style={{ fontSize: '12px', color: '#64748b' }}>{p.email}</div>}
                        </div>
                        <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#6366f1', fontWeight: '600' }}>
                          {p?.bp_count ?? 0} BPs {selectedRole?.key === 'rm_id' && p?.sm_count !== undefined ? `(${p.sm_count} SMs)` : ''}
                        </span>
                      </div>
                    );
                  })
                ) : (
                  <>
                    {Object.keys(rowSelectionBp).slice(0, 3).map((id) => {
                      const p = allFetchedPartners.find(x => String(x.partnership_id) === String(id));
                      return (
                        <div key={`bp-${id}`} style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ background: '#e0e7ff', color: '#4f46e5', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontWeight: '700' }}>BP</span>
                          <span style={{ fontSize: '15px', color: '#334155', fontWeight: '600' }}>{p ? p.name : `ID ${id}`}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>{p ? p.partnership_code : ''}</span>
                        </div>
                      );
                    })}
                    {Object.keys(rowSelectionBa).slice(0, 3 - Math.min(Object.keys(rowSelectionBp).length, 3)).map((id) => {
                      const p = allFetchedConsolidators.find(x => String(x.consolidator_id) === String(id));
                      return (
                        <div key={`ba-${id}`} style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: '16px' }}>
                          <span style={{ background: '#e0f2fe', color: '#0ea5e9', fontSize: '12px', padding: '4px 8px', borderRadius: '6px', fontWeight: '700' }}>BA</span>
                          <span style={{ fontSize: '15px', color: '#334155', fontWeight: '600' }}>{p ? p.name : `ID ${id}`}</span>
                          <span style={{ marginLeft: 'auto', fontSize: '13px', color: '#94a3b8', fontWeight: '500' }}>{p ? p.code : ''}</span>
                        </div>
                      );
                    })}
                  </>
                )}
                {(Object.keys(rowSelectionBp).length + (selectedRole?.key === 'sm_id' ? Object.keys(rowSelectionBa).length : 0) > (selectedRole?.key === 'sm_id' ? 3 : 5)) && (
                  <div style={{ padding: '16px', color: '#94a3b8', fontSize: '14px', fontStyle: 'italic', textAlign: 'center', background: '#fafafa' }}>
                    ...and {(Object.keys(rowSelectionBp).length + (selectedRole?.key === 'sm_id' ? Object.keys(rowSelectionBa).length : 0)) - (selectedRole?.key === 'sm_id' ? 3 : 5)} more records
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{ padding: '20px 32px', background: 'white', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'flex-end', gap: '16px' }}>
            <button onClick={() => setIsReviewModalOpen(false)} style={{ padding: '12px 24px', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s' }}>Cancel</button>
            <button onClick={handleConfirmReassign} disabled={isSubmitting} style={{ padding: '12px 32px', borderRadius: '8px', border: 'none', background: '#4f46e5', color: 'white', fontWeight: '600', fontSize: '15px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', boxShadow: '0 4px 6px -1px rgba(79, 70, 229, 0.2)', transition: 'all 0.2s', opacity: isSubmitting ? 0.7 : 1 }}>
              {isSubmitting ? (
                 <><div className="rr-spinner rr-spinner--sm" style={{borderColor: 'rgba(255,255,255,0.3)', borderTopColor: 'white'}}/> Generating Ticket...</>
              ) : (
                 <>Submit to HO for Approval <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 12h14M12 5l7 7-7 7"/></svg></>
              )}
            </button>
          </div>
        </div>
      </Modal>

    </Fragment>
  );
};

export default RoleReassignment;
